import React, { FC, useEffect } from 'react'
import styled, { css } from 'styled-components'

import Box, { Flex } from 'components/Box'
import Caption from 'components/Caption'
import { AsyncButton } from 'components/Button'
import Spinner from 'components/Spinner'
import ErrorMessage from 'components/ErrorMessage'
import InfiniteList from 'components/InfiniteList'
import LoadingPlaceholder from 'components/LoadingPlaceholder'

import NotificationListItem from './NotificationListItem'
import {
  Wrapper,
  LoadingWrapper,
} from 'modules/user/views/Updates/Updates.styles'

import { ReactComponent as BellIcon } from 'icons/bell-filled-64.svg'

import { NotificationInfoFragment } from 'gqlTypes'

import { breakpoint } from 'utils'

import { useToast } from 'containers/ToastManager'

/**
 * TYPES
 */
interface NotificationsListProps {
  loading: boolean
  loadMore: () => Promise<any>
  onClickNotification?: () => void
  entries?: NotificationInfoFragment[]
  subscribeToMore: () => void
  markNotificationAsRead: (id: string) => void
  markAllNotificationAsSeen: () => void
  markAllNotificationsAsRead: () => void
  hasUnreadNotifications?: boolean
}

/**
 * STYLES
 */
const MarkAllAsReadButton = styled(AsyncButton).attrs({ tiny: true })`
  margin-left: 12px;
`

const LoadMoreIndicator = styled(props => (
  <Flex py={2} justifyContent="center" {...props}>
    <Spinner primary />
  </Flex>
))`
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.colors.border.dark.base};
`

const NotificationsHeading = styled.div(
  ({ theme }) => css`
    margin: 0 12px 24px 24px;
    font-size: ${theme.fontSizes.large};
    font-weight: ${theme.fontWeights.bold};

    ${breakpoint('xs')`
      margin-left: 0;
    `}
  `
)

/**
 * MAIN COMPONENT
 */
const NotificationsList: FC<NotificationsListProps> = ({
  subscribeToMore,
  hasUnreadNotifications,
  markAllNotificationAsSeen,
  markNotificationAsRead,
  entries,
  markAllNotificationsAsRead,
  loading,
  loadMore,
  onClickNotification = () => {},
}) => {
  const { showToast } = useToast()

  useEffect(() => {
    subscribeToMore()
    markAllNotificationAsSeen()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const renderList = () => {
    if (loading && !entries)
      return (
        <LoadingWrapper>
          <LoadingPlaceholder size="64px" />
        </LoadingWrapper>
      )

    if (entries?.length === 0) {
      return (
        <Box width="100%" height="100%" px={6} UNSAFE_py="50px">
          <ErrorMessage
            icon={<BellIcon />}
            iconSize="medium"
            title="No unread notifications"
            description="Activity on documents you’re involved in will appear here. "
          />
        </Box>
      )
    }

    const { unreadNotifications, readNotifications } = splitNotificationTypes(
      entries
    )

    const renderNotification = (notification: NotificationInfoFragment) => (
      <NotificationListItem
        key={notification.identifier}
        onClick={event => {
          if (!event.ctrlKey && !event.metaKey) {
            onClickNotification()
          }

          if (
            notification.__typename === 'CommentNotification' &&
            !notification.path
          ) {
            showToast('This reply has been deleted', 'negative')
          }

          if (notification.isRead) {
            return
          }

          markNotificationAsRead(notification.identifier)
        }}
        isRead={!!notification.isRead}
        notification={notification}
        markNotificationAsRead={() => {
          markNotificationAsRead(notification.identifier)
        }}
        isVisuallyRead={notification.isRead || false}
      />
    )

    return (
      <InfiniteList
        onLoadMore={loadMore}
        renderLoading={() => <LoadMoreIndicator />}>
        <Wrapper>
          <NotificationsHeading data-testid="notifications-unread-title">
            <Flex>
              <div>Unread</div>
              <MarkAllAsReadButton
                onClick={markAllNotificationsAsRead}
                disabled={!hasUnreadNotifications}>
                Mark All as Read
              </MarkAllAsReadButton>
            </Flex>
          </NotificationsHeading>
          {unreadNotifications.length ? (
            unreadNotifications.map(renderNotification)
          ) : (
            <Box px={2} py={6}>
              <Caption fontSize="small" textAlign="center" m={0}>
                {'No unread notifications.'}
              </Caption>
            </Box>
          )}

          <NotificationsHeading data-testid="notifications-read-title">
            <Flex>Read</Flex>
          </NotificationsHeading>
          {readNotifications.length ? (
            readNotifications.map(renderNotification)
          ) : (
            <Box px={2} py={6}>
              <Caption fontSize="small" textAlign="center" m={0}>
                {'No read notifications.'}
              </Caption>
            </Box>
          )}
        </Wrapper>
      </InfiniteList>
    )
  }

  return renderList()
}

export default NotificationsList

function splitNotificationTypes(
  notifications: NotificationInfoFragment[] = []
) {
  const notificationsFormat: {
    unreadNotifications: NotificationInfoFragment[]
    readNotifications: NotificationInfoFragment[]
  } = {
    unreadNotifications: [],
    readNotifications: [],
  }

  return (
    notifications.reduce(
      ({ unreadNotifications, readNotifications }, notification) => {
        if (notification.isRead) {
          readNotifications.push(notification)
        } else {
          unreadNotifications.push(notification)
        }

        return { unreadNotifications, readNotifications }
      },
      notificationsFormat
    ) || notificationsFormat
  )
}
